import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { normalize } from '../../utils/normalize';
import { canWrite } from '../../utils/roles';
import ItemFormModal from './ItemFormModal';
import ItemDetailModal from './ItemDetailModal';
import useScrollRestoration from '../../hooks/useScrollRestoration';
import './InventoryPage.css';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconChevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconWarning = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconPack = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconMap = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconSettings = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelSortKey(label) {
  if (!label) return { n: Infinity, s: '' };
  const m = label.match(/^(\d+)$/);
  return m ? { n: parseInt(m[1], 10), s: '' } : { n: Infinity, s: label };
}

function groupByName(items) {
  const groups = {};
  for (const item of items) {
    if (!groups[item.name]) groups[item.name] = [];
    groups[item.name].push(item);
  }
  for (const name of Object.keys(groups)) {
    groups[name].sort((a, b) => {
      const ka = labelSortKey(a.unit_label);
      const kb = labelSortKey(b.unit_label);
      if (ka.n !== kb.n) return ka.n - kb.n;
      return ka.s.localeCompare(kb.s, 'es');
    });
  }
  return groups;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-UY', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23',
  });
}

function inventoryCode(prefix, record) {
  return record.code || `${prefix}-${String(record.id).padStart(4, '0')}`;
}

function StatusBadge({ item }) {
  if (item.has_open_incident) return <span className="badge badge-yellow">Incidencia</span>;
  if (!item.is_operational) return <span className="badge badge-red">No operativo</span>;
  return <span className="badge badge-green">Operativo</span>;
}

// ─── Incident Report Modal ────────────────────────────────────────────────────

const STATUS_LABELS = {
  abierta: 'Abierta',
  en_reparacion: 'En reparación',
  resuelta: 'Resuelta',
};
const STATUS_BADGE = {
  abierta: 'badge-red',
  en_reparacion: 'badge-yellow',
  resuelta: 'badge-green',
};

function IncidentModal({ target, onClose, onSaved }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ description: '', can_operate: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusSaving, setStatusSaving] = useState(null);

  const endpoint = target.type === 'item' ? 'items' : 'packs';

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await api.get(`/inventory/${endpoint}/${target.id}`);
      setIncidents(res.data.incidents || []);
    } catch {
      // ignore, incidents stay empty
    } finally {
      setLoading(false);
    }
  }, [endpoint, target.id]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const handleStatusChange = async (incidentId, newStatus) => {
    setStatusSaving(incidentId);
    try {
      await api.put(`/inventory/incidents/${incidentId}`, { status: newStatus });
      await fetchIncidents();
      onSaved();
    } catch {
      // silent — status reverts on re-fetch
    } finally {
      setStatusSaving(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/inventory/incidents', {
        [target.type === 'item' ? 'item_id' : 'pack_id']: target.id,
        description: form.description,
        can_operate: form.can_operate,
      });
      setForm({ description: '', can_operate: false });
      await fetchIncidents();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reportar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-title">Incidencias</div>
        <div className="text-dim text-sm" style={{ marginBottom: 16 }}>{target.name}</div>

        {loading ? (
          <div className="text-dim text-sm" style={{ marginBottom: 16 }}>Cargando...</div>
        ) : incidents.length === 0 ? (
          <div className="text-dim text-sm" style={{ marginBottom: 16 }}>Sin incidencias registradas.</div>
        ) : (
          <div className="inv-incidents-list" style={{ marginBottom: 20 }}>
            {incidents.map(inc => (
              <div key={inc.id} className="inv-incident-card">
                <div className="inv-incident-header">
                  <span className={`badge ${STATUS_BADGE[inc.status]}`}>{STATUS_LABELS[inc.status]}</span>
                  <span className="text-dim text-sm">{new Date(inc.reported_at).toLocaleDateString('es-AR')}</span>
                  <span className="text-dim text-sm">{inc.reported_by_name}</span>
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{inc.description}</div>
                {inc.status === 'resuelta' && inc.resolved_by_name && (
                  <div className="text-dim text-sm" style={{ marginBottom: 8 }}>
                    Resuelta por {inc.resolved_by_name}
                  </div>
                )}
                <select
                  className="input"
                  style={{ fontSize: 13, padding: '4px 8px' }}
                  value={inc.status}
                  disabled={statusSaving === inc.id}
                  onChange={e => handleStatusChange(inc.id, e.target.value)}
                >
                  <option value="abierta">Abierta</option>
                  <option value="en_reparacion">En reparación</option>
                  <option value="resuelta">Resuelta</option>
                </select>
              </div>
            ))}
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div className="form-label" style={{ marginBottom: 10 }}>Nueva incidencia</div>
          <form onSubmit={handleSubmit}>
            {error && <div className="inv-error">{error}</div>}
            <div className="form-group">
              <textarea
                className="input"
                rows={3}
                placeholder="¿Qué pasó con el equipo?"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                required
                style={{ resize: 'vertical' }}
              />
            </div>
            <label className="inv-checkbox-label" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={form.can_operate}
                onChange={e => setForm(f => ({ ...f, can_operate: e.target.checked }))}
              />
              El equipo puede seguir operando
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cerrar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Reportar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Pack Modal (create / edit + manage items) ────────────────────────────────

function PackModal({ pack, allItems, onClose, onSaved }) {
  const isEdit = !!pack;
  const [name, setName] = useState(pack?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [packItems, setPackItems] = useState(pack?.items || []);
  const [addSearch, setAddSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const availableToAdd = allItems.filter(
    i => !i.pack_id && !packItems.find(pi => pi.id === i.id)
  );
  const filteredAvailable = addSearch
    ? availableToAdd.filter(i => {
        const words = normalize(addSearch).split(/\s+/).filter(Boolean);
        return words.every(w => normalize(i.name).includes(w));
      })
    : availableToAdd;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      // PackModal es solo edición: la creación de packs va por ItemFormModal
      // (mode='pack'). El pack siempre existe acá.
      await api.put(`/inventory/packs/${pack.id}`, {
        name: name.trim(),
        quantity: pack.quantity ?? 0,
        notes: pack.notes || null,
        category: pack.category || null,
        subcategory: pack.subcategory || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category,
        subcategory: item.subcategory, quantity: item.quantity,
        notes: item.notes || null, pack_id: null,
        unit_label: item.unit_label || null,
      });
      setPackItems(prev => prev.filter(i => i.id !== item.id));
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al quitar ítem');
    }
  };

  const handleAddItem = async (item) => {
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category,
        subcategory: item.subcategory, quantity: item.quantity,
        notes: item.notes || null, pack_id: pack.id,
        unit_label: item.unit_label || null,
      });
      setPackItems(prev => [...prev, item]);
      setAddSearch('');
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar ítem');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{isEdit ? 'Editar pack' : 'Nuevo pack'}</div>
        <form onSubmit={handleSave}>
          {error && <div className="inv-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Nombre del pack</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: isEdit ? 20 : 0 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : isEdit ? 'Guardar nombre' : 'Crear pack'}
            </button>
          </div>
        </form>

        {isEdit && (
          <>
            <div className="divider" />
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
              Ítems en el pack ({packItems.length})
            </div>
            {packItems.length === 0 ? (
              <p className="text-dim text-sm" style={{ marginBottom: 12 }}>Sin ítems asignados</p>
            ) : (
              <div className="inv-pack-items-list">
                {packItems.map(item => (
                  <div key={item.id} className="inv-pack-item-row">
                    <div style={{ minWidth: 0 }}>
                      <div className="text-sm" style={{ fontWeight: 500 }}>{item.name}</div>
                      <div className="text-xs text-dim">{item.subcategory}</div>
                    </div>
                    <button
                      className="btn btn-icon btn-ghost"
                      style={{ color: 'var(--red)', flexShrink: 0 }}
                      onClick={() => handleRemoveItem(item)}
                      title="Quitar del pack"
                    >
                      <IconX />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showAdd ? (
              <div style={{ marginTop: 8 }}>
                <input
                  className="input"
                  style={{ marginBottom: 8 }}
                  placeholder="Buscar ítem para agregar..."
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  autoFocus
                />
                {filteredAvailable.length === 0 ? (
                  <p className="text-dim text-sm">No hay ítems disponibles sin pack</p>
                ) : (
                  <div className="inv-pack-items-list inv-pack-items-list--scroll">
                    {filteredAvailable.map(item => (
                      <div
                        key={item.id}
                        className="inv-pack-item-row inv-pack-item-add"
                        onClick={() => handleAddItem(item)}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="text-sm" style={{ fontWeight: 500 }}>{item.name}</div>
                          <div className="text-xs text-dim">{item.category} · {item.subcategory}</div>
                        </div>
                        <IconPlus />
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => { setShowAdd(false); setAddSearch(''); }}
                >
                  Cerrar
                </button>
              </div>
            ) : (
              availableToAdd.length > 0 && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setShowAdd(true)}
                >
                  <IconPlus /> Agregar ítem
                </button>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ name, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">¿Eliminar?</h3>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
          Vas a eliminar permanentemente:
        </p>
        <p style={{ fontWeight: 600, marginBottom: 24 }}>{name}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleConfirm} disabled={busy}>
            {busy ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Location History Modal ───────────────────────────────────────────────────

function LocationHistoryModal({ target, onClose }) {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    const url = target._type === 'pack'
      ? `/inventory/packs/${target.id}/location-history`
      : `/inventory/items/${target.id}/location-history`;
    api.get(url)
      .then(r => setHistory(r.data))
      .catch(() => setHistory([]));
  }, [target.id, target._type]);

  const title = target.unit_label ? `${target.name} ${target.unit_label}` : target.name;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ marginBottom: 16, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          Historial de ubicaciones
          <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-dim)' }}>{title}</span>
        </div>
        {history === null ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '20px 0' }}>Cargando...</div>
        ) : history.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '20px 0' }}>Este equipo no tiene movimientos registrados.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
            {history.map(h => (
              <div key={h.id} style={{
                padding: '10px 12px', borderRadius: 6,
                background: 'var(--surface2)', border: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{h.vehicle || '—'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{fmtDateTime(h.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 3 }}>
                  {h.from_location_name} → {h.to_location_name}
                </div>
                {h.created_by_name && (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>Por {h.created_by_name}</div>
                )}
                {h.event_name && (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>Evento: {h.event_name}</div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Consulta Tab ─────────────────────────────────────────────────────────────

function ConsultaTab({ items, canReport, isAdmin, categories = [], onReportItem, onViewItem, onUpdateItem, onDeleteItem, onManageCategories, onViewLocationHistory }) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todas');
  const [onlyIncidents, setOnlyIncidents] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');
  const [savingLabelId, setSavingLabelId] = useState(null);

  const handleSaveLabel = async (item) => {
    setSavingLabelId(item.id);
    try {
      await api.put(`/inventory/items/${item.id}`, {
        name: item.name, category: item.category, subcategory: item.subcategory,
        quantity: item.quantity, notes: item.notes || null,
        pack_id: item.pack_id || null, unit_label: editingLabelValue.trim() || null,
      });
      onUpdateItem({ ...item, unit_label: editingLabelValue.trim() || null });
      setEditingLabelId(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSavingLabelId(null);
    }
  };

  const rows = useMemo(() => {
    const words = normalize(search).split(/\s+/).filter(Boolean);
    return items.filter(item => {
      const content = `${item.name} ${item.unit_label || ''} ${item.category || ''} ${item.subcategory || ''}`;
      return (activeCategory === 'Todas' || item.category === activeCategory) &&
        (!onlyIncidents || item.has_open_incident) &&
        (!words.length || words.every(word => normalize(content).includes(word)));
    }).sort((a, b) => a.name.localeCompare(b.name, 'es') || (a.unit_label || '').localeCompare(b.unit_label || '', 'es'));
  }, [items, search, onlyIncidents, activeCategory]);

  const availableCategories = useMemo(() => {
    const inData = new Set([
      ...items.map(i => i.category).filter(Boolean),
    ]);
    const order = categories.length ? categories.map(c => c.name) : ['Audio', 'Iluminación', 'Video', 'IT / Redes', 'Estructura'];
    return order.filter(c => inData.has(c));
  }, [items, categories]);

  return (
    <div>
      <div className="inv-toolbar">
        <div className="inv-search-wrapper">
          <span className="inv-search-icon"><IconSearch /></span>
          <input
            className="input"
            placeholder="Buscar equipo..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`btn btn-ghost btn-sm inv-incidents-toggle${onlyIncidents ? ' active-tab' : ''}`}
          onClick={() => setOnlyIncidents(v => !v)}
        >
          <IconWarning /> Con incidencias
        </button>
        <Link to="/inventory/disponibilidad" className="btn btn-ghost btn-sm">
          Disponibilidad
        </Link>
      </div>

      <div className="inv-category-tabs">
        {['Todas', ...availableCategories].map(cat => (
          <button
            key={cat}
            className={`btn btn-ghost btn-sm${activeCategory === cat ? ' active-tab' : ''}`}
            onClick={() => setActiveCategory(activeCategory === cat && cat !== 'Todas' ? 'Todas' : cat)}
          >
            {cat}
          </button>
        ))}
        {isAdmin && (
          <button
            className="btn btn-icon btn-ghost btn-sm"
            title="Gestionar categorías"
            style={{ marginLeft: 'auto', flexShrink: 0 }}
            onClick={onManageCategories}
          ><IconSettings /></button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="inv-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          <p>Sin resultados</p>
        </div>
      ) : (
        <div className="inv-table inv-stock-table">
          <div className="inv-table-head inv-stock-table-head"><span>Código</span><span>Artículo</span><span>Etiqueta</span><span>Categoría</span><span>Estado</span><span>Ubicación</span><span /></div>
          {rows.map(item => (
            <div key={item.id} className="inv-table-row inv-stock-table-row" onClick={() => editingLabelId !== item.id && onViewItem(item)}>
              <span className="inv-code">{inventoryCode('A', item)}</span>
              <strong>{item.name}</strong>
              <span onClick={event => event.stopPropagation()}>
                {editingLabelId === item.id ? <div className="inv-label-editor"><input className="input" autoFocus value={editingLabelValue} onChange={event => setEditingLabelValue(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') handleSaveLabel(item); if (event.key === 'Escape') setEditingLabelId(null); }} /><button className="btn btn-icon btn-ghost" onClick={() => handleSaveLabel(item)} disabled={savingLabelId === item.id}><IconCheck /></button><button className="btn btn-icon btn-ghost" onClick={() => setEditingLabelId(null)}><IconX /></button></div> : item.unit_label || '—'}
              </span>
              <span>{item.category || 'Sin categoría'}{item.subcategory ? ` · ${item.subcategory}` : ''}</span>
              <span><StatusBadge item={item} /></span>
              <span>{item.pack_id && item.pack_name ? `Rack: ${item.pack_name}` : item.is_at_home === false && item.current_location_name ? item.current_location_name : 'Depósito'}</span>
              <span className="inv-table-actions" onClick={event => event.stopPropagation()}>
                <button className="btn btn-icon btn-ghost" title="Historial de ubicaciones" onClick={() => onViewLocationHistory(item)}><IconMap /></button>
                {canReport && <button className="btn btn-icon btn-ghost" title="Reportar incidencia" onClick={() => onReportItem(item)}><IconWarning /></button>}
                {isAdmin && editingLabelId !== item.id && <><button className="btn btn-icon btn-ghost" title="Editar etiqueta" onClick={() => { setEditingLabelId(item.id); setEditingLabelValue(item.unit_label || ''); }}><IconPencil /></button><button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} title="Eliminar" onClick={() => onDeleteItem(item)}><IconTrash /></button></>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Categories Modal ─────────────────────────────────────────────────────────

function CategoriesModal({ categories, onClose, onSaved }) {
  const [expandedCats, setExpandedCats] = useState(() => new Set(categories.map(c => c.id)));
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [addingSubToCat, setAddingSubToCat] = useState(null);
  const [newSubName, setNewSubName] = useState('');
  const [deletingCat, setDeletingCat] = useState(null);
  const [deletingSub, setDeletingSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleCat = id => setExpandedCats(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const doSave = async (fn) => {
    setSaving(true); setError('');
    try { await fn(); onSaved(); } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleSaveCat = (cat) => doSave(async () => {
    if (!editingCatName.trim()) return;
    await api.put(`/inventory/categories/${cat.id}`, { name: editingCatName.trim(), sort_order: cat.sort_order });
    setEditingCatId(null);
  });

  const handleSaveSub = (catId, sub) => doSave(async () => {
    if (!editingSubName.trim()) return;
    await api.put(`/inventory/categories/${catId}/subcategories/${sub.id}`, { name: editingSubName.trim(), sort_order: sub.sort_order });
    setEditingSubId(null);
  });

  const handleAddCat = () => doSave(async () => {
    if (!newCatName.trim()) return;
    await api.post('/inventory/categories', { name: newCatName.trim(), sort_order: categories.length });
    setNewCatName('');
  });

  const handleAddSub = (catId) => doSave(async () => {
    if (!newSubName.trim()) return;
    const cat = categories.find(c => c.id === catId);
    await api.post(`/inventory/categories/${catId}/subcategories`, { name: newSubName.trim(), sort_order: cat?.subcategories?.length || 0 });
    setNewSubName(''); setAddingSubToCat(null);
  });

  const handleDeleteCat = (cat) => doSave(async () => {
    await api.delete(`/inventory/categories/${cat.id}`);
    setDeletingCat(null);
  });

  const handleDeleteSub = (catId, sub) => doSave(async () => {
    await api.delete(`/inventory/categories/${catId}/subcategories/${sub.id}`);
    setDeletingSub(null);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520, maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Gestionar categorías</div>
        {error && <div className="inv-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20 }}>
          {categories.map(cat => (
            <div key={cat.id}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}
                onClick={() => toggleCat(cat.id)}
              >
                <IconChevron open={expandedCats.has(cat.id)} />
                {editingCatId === cat.id ? (
                  <div style={{ display: 'flex', flex: 1, gap: 6 }} onClick={e => e.stopPropagation()}>
                    <input
                      className="input"
                      style={{ flex: 1, padding: '2px 8px', height: 30, fontSize: 13 }}
                      value={editingCatName}
                      onChange={e => setEditingCatName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveCat(cat); if (e.key === 'Escape') setEditingCatId(null); }}
                      autoFocus
                    />
                    <button className="btn btn-icon btn-ghost" onClick={() => handleSaveCat(cat)} disabled={saving}><IconCheck /></button>
                    <button className="btn btn-icon btn-ghost" onClick={() => setEditingCatId(null)}><IconX /></button>
                  </div>
                ) : (
                  <>
                    <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{cat.name}</span>
                    <span className="text-dim text-xs" style={{ flexShrink: 0 }}>{cat.subcategories?.length || 0} subs</span>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }}><IconPencil /></button>
                      <button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} title="Eliminar" onClick={() => setDeletingCat(cat)}><IconTrash /></button>
                    </div>
                  </>
                )}
              </div>

              {expandedCats.has(cat.id) && (
                <div style={{ paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
                  {cat.subcategories?.map(sub => (
                    <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      {editingSubId === sub.id ? (
                        <div style={{ display: 'flex', flex: 1, gap: 6 }}>
                          <input
                            className="input"
                            style={{ flex: 1, padding: '2px 8px', height: 28, fontSize: 13 }}
                            value={editingSubName}
                            onChange={e => setEditingSubName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveSub(cat.id, sub); if (e.key === 'Escape') setEditingSubId(null); }}
                            autoFocus
                          />
                          <button className="btn btn-icon btn-ghost" onClick={() => handleSaveSub(cat.id, sub)} disabled={saving}><IconCheck /></button>
                          <button className="btn btn-icon btn-ghost" onClick={() => setEditingSubId(null)}><IconX /></button>
                        </div>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 13 }}>{sub.name}</span>
                          <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => { setEditingSubId(sub.id); setEditingSubName(sub.name); }}><IconPencil /></button>
                          <button className="btn btn-icon btn-ghost" style={{ color: 'var(--red)' }} title="Eliminar" onClick={() => setDeletingSub({ catId: cat.id, sub })}><IconTrash /></button>
                        </>
                      )}
                    </div>
                  ))}

                  {addingSubToCat === cat.id ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <input
                        className="input"
                        style={{ flex: 1, padding: '2px 8px', height: 28, fontSize: 13 }}
                        placeholder="Nombre de subcategoría..."
                        value={newSubName}
                        onChange={e => setNewSubName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleAddSub(cat.id);
                          if (e.key === 'Escape') { setAddingSubToCat(null); setNewSubName(''); }
                        }}
                        autoFocus
                      />
                      <button className="btn btn-icon btn-ghost" onClick={() => handleAddSub(cat.id)} disabled={saving || !newSubName.trim()}><IconCheck /></button>
                      <button className="btn btn-icon btn-ghost" onClick={() => { setAddingSubToCat(null); setNewSubName(''); }}><IconX /></button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, width: 'fit-content' }}
                      onClick={() => { setAddingSubToCat(cat.id); setNewSubName(''); }}
                    >
                      <IconPlus /> Nueva subcategoría
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="divider" style={{ marginBottom: 16 }} />
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Nueva categoría</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            style={{ flex: 1 }}
            placeholder="Nombre de categoría..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddCat(); }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleAddCat} disabled={saving || !newCatName.trim()}>
            <IconPlus /> Agregar
          </button>
        </div>

        {deletingCat && (
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--red)', borderRadius: 8, padding: 16, marginTop: 16 }}>
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              ¿Eliminar <strong>{deletingCat.name}</strong>? Los ítems y packs pasarán a "Genéricos".
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeletingCat(null)}>Cancelar</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCat(deletingCat)} disabled={saving}>Eliminar</button>
            </div>
          </div>
        )}

        {deletingSub && (
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--red)', borderRadius: 8, padding: 16, marginTop: 16 }}>
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              ¿Eliminar <strong>{deletingSub.sub.name}</strong>? Los ítems quedarán sin subcategoría.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeletingSub(null)}>Cancelar</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSub(deletingSub.catId, deletingSub.sub)} disabled={saving}>Eliminar</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  useScrollRestoration('inventory');
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = canWrite(user, 'inventario.stock');
  const canReport = false;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // undefined = cerrado, null = nuevo, objeto = editar
  const [incidentTarget, setIncidentTarget] = useState(null);
  const [itemFormTarget, setItemFormTarget] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [viewItemUnitCount, setViewItemUnitCount] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [locationHistoryTarget, setLocationHistoryTarget] = useState(null);
  const [newMenuOpen, setNewMenuOpen] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/inventory/categories');
      setCategories(res.data);
    } catch (e) { console.error(e); }
  }, []);

  // silent: refresco tras un guardado — no muestra el spinner para no desmontar
  // ConsultaTab (perdería búsqueda y grupos expandidos). loadSeq: guard last-wins
  // para que una respuesta vieja no pise a una más nueva si se solapan.
  const loadSeq = useRef(0);
  const load = useCallback(async ({ silent = false } = {}) => {
    const seq = ++loadSeq.current;
    if (!silent) setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        api.get('/inventory/items'),
        api.get('/inventory/categories'),
      ]);
      if (seq !== loadSeq.current) return;
      setItems(itemsRes.data);
      setCategories(catsRes.data);
    } catch (e) {
      if (seq === loadSeq.current) console.error(e);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/inventory/items/${deleteTarget.id}`);
      setDeleteTarget(null);
      load({ silent: true });
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Stock</h1>
          <p className="page-subtitle">{items.length} artículos unitarios</p>
        </div>
        {(canWrite(user, 'inventario.stock') || canWrite(user, 'inventario.racks') || canWrite(user, 'inventario.packs')) && (
          <div className="inv-new-menu-wrap">
            <button className="btn btn-primary" onClick={() => setNewMenuOpen(open => !open)}>+ Nuevo</button>
            {newMenuOpen && <div className="inv-new-menu">
              {canWrite(user, 'inventario.stock') && <button onClick={() => { setItemFormTarget(null); setNewMenuOpen(false); }}>Artículo</button>}
              {canWrite(user, 'inventario.racks') && <button onClick={() => { navigate('/inventory/racks'); setNewMenuOpen(false); }}>Rack</button>}
              {canWrite(user, 'inventario.packs') && <button onClick={() => { navigate('/inventory/packs'); setNewMenuOpen(false); }}>Pack</button>}
            </div>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center" style={{ gap: 12, padding: '40px 0', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ width: 20, height: 20 }} />
          Cargando inventario...
        </div>
      ) : (
        <ConsultaTab
          items={items}
          canReport={canReport}
          isAdmin={isAdmin}
          categories={categories}
          onReportItem={item => setIncidentTarget({ type: 'item', id: item.id, name: item.name })}
          onViewItem={(item, unitCount) => { setViewItem(item); setViewItemUnitCount(unitCount ?? null); }}
          onUpdateItem={updated => setItems(prev => prev.map(i => i.id === updated.id ? updated : i))}
          onDeleteItem={item => setDeleteTarget({ type: 'item', id: item.id, name: item.name })}
          onManageCategories={() => setShowCategoriesModal(true)}
          onViewLocationHistory={item => setLocationHistoryTarget({ ...item, _type: 'item' })}
        />
      )}

      {/* ── Modals ── */}
      {incidentTarget && (
        <IncidentModal
          target={incidentTarget}
          onClose={() => setIncidentTarget(null)}
          onSaved={() => load({ silent: true })}
        />
      )}

      {itemFormTarget !== undefined && (
        <ItemFormModal
          item={itemFormTarget}
          categories={categories}
          onClose={saved => { setItemFormTarget(undefined); if (saved) load({ silent: true }); }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          name={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showCategoriesModal && (
        <CategoriesModal
          categories={categories}
          onClose={() => setShowCategoriesModal(false)}
          onSaved={loadCategories}
        />
      )}

      {viewItem && (
        <ItemDetailModal
          item={viewItem}
          unitCount={viewItemUnitCount}
          onClose={shouldRefresh => { setViewItem(null); setViewItemUnitCount(null); if (shouldRefresh) load(); }}
          onEdit={isAdmin ? item => { setViewItem(null); setItemFormTarget(item); } : null}
        />
      )}

      {locationHistoryTarget && (
        <LocationHistoryModal
          target={locationHistoryTarget}
          onClose={() => setLocationHistoryTarget(null)}
        />
      )}
    </div>
  );
}
